/**
 * Cookie Consent + PostHog dual-mode analytics.
 *
 * Policy LPD (Svizzera) + GDPR (UE):
 * - PostHog NON parte automaticamente: è in modalità "anonima" fino a esplicito consenso.
 * - Anonymous mode:
 *     • persistence='memory' → nessun cookie persistente, stato perso al chiudere tab
 *     • disable_session_recording
 *     • no user identification
 *     • ip masking applicato
 *     • no autocapture events (solo pageviews aggregati)
 * - Consented mode:
 *     • persistence='localStorage+cookie', session recording ON, identify ON
 *
 * Rationale: tracking aggregato anonimo non richiede consenso esplicito
 * (analytics strettamente necessari), ma ogni identificatore persistente sì.
 */

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let _mode = null;

/**
 * Inizializza PostHog in anonymous mode. Chiamato al boot dell'app se no-consent.
 * Idempotente: se già init come 'anonymous' → skip. Se era 'full' e diventa 'anonymous'
 * → reset e re-init.
 */
export function initAnonymousAnalytics() {
  if (typeof window === 'undefined' || !window.posthog) return;
  if (_mode === 'anonymous') return;
  try {
    if (_mode === 'full') {
      try { window.posthog.reset(true); } catch {}
    }
    window.posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      persistence: 'memory',           // no cookie persistente
      disable_session_recording: true,
      autocapture: false,              // solo pageview manuali
      capture_pageview: true,
      disable_surveys: true,
      mask_all_text: true,
      mask_all_element_attributes: true,
      ip: false,                       // PostHog server IP-masking (last octet)
      person_profiles: 'never',
      opt_out_capturing_by_default: false,
      disable_compression: false,
    });
    _mode = 'anonymous';
  } catch (e) {
    // Silenzioso — analytics non deve mai rompere l'app
    console.debug('[analytics] init anonymous failed', e);
  }
}

/**
 * Upgrade a full tracking mode dopo consenso utente.
 */
export function initFullAnalytics() {
  if (typeof window === 'undefined' || !window.posthog) return;
  if (_mode === 'full') return;
  try {
    // Reset anonymous state prima di re-init con persistenza
    if (_mode === 'anonymous') {
      try { window.posthog.reset(true); } catch {}
    }
    window.posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      persistence: 'localStorage+cookie',
      session_recording: {
        recordCrossOriginIframes: true,
        capturePerformance: false,
      },
      autocapture: true,
      capture_pageview: true,
      person_profiles: 'identified_only',
      ip: true,
    });
    _mode = 'full';
  } catch (e) {
    console.debug('[analytics] init full failed', e);
  }
}

/**
 * Rifiuto consenso: resetta qualunque stato identificativo e torna in anonymous.
 */
export function revokeAnalytics() {
  if (typeof window === 'undefined' || !window.posthog) return;
  try {
    window.posthog.reset(true);
    window.posthog.opt_out_capturing?.();
  } catch {}
  _mode = null;
  initAnonymousAnalytics();
}

export function getMode() { return _mode; }

/**
 * Cattura un custom event (best-effort, no-op se PostHog non è inizializzato).
 * Usato per tracciare problemi tipo skeleton stuck, tab freeze, etc.
 */
export function trackEvent(eventName, properties = {}) {
  if (typeof window === 'undefined' || !window.posthog) return;
  try {
    window.posthog.capture(eventName, {
      ...properties,
      ua: navigator.userAgent,
      url: window.location.pathname,
    });
  } catch {}
}
